import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"
import { useSortBy, useTable, usePagination } from "react-table"
import { pdf } from "@react-pdf/renderer"
import { useTranslation } from "react-i18next"
import { TFunction } from "i18next"
import { SearchBikeFleet } from "./SearchBikeFleet"
import ControlPagingBikeFleet from "./ControlPagingBikeFleet"
import BodyTableBikeFleet from "./BodyTableBikeFleet"
import { HeaderTableBikeFleet } from "./HeaderTableBikeFleet"
import { useColumnSize } from "features/bikeFleet/hooks/useColumnSize"
import { useColumn } from "features/bikeFleet/hooks/useColumn"
import { useAppDispatch, useAppSelector } from "store/hooks"
import {
	setToggleCheckedAll,
	setToggleCheckedByCodeFnucy,
} from "features/bikeFleet/slice/bikeFleetSlice"
import { useCurrentSearchParams } from "hooks/useCurrentSearchParams"
import { defaultBikeFleetQuery } from "features/bikeFleet/constant"
import { MessageStatusBikeFleet } from "./MessageStatusBikeFleet"
import dateService from "services/date"
import saveBlob from "utils/saveBlob"
import { useAlert, useAuth } from "hooks"
import BikePdf from "../pdf/BikePdf"
import { Bike } from "models"
import StatusBikeFleet from "../StatusBikeFormFleet"
import { BikeFleetType, Column } from "features/bikeFleet/types/bikeFleetType"
import NoBikeFleet from "./NoBikeFleet"
import { isNotEmpty } from "utils"

const TableBikeFleet = ({ datas }: { datas?: BikeFleetType[] }, ref) => {
	const dispatch = useAppDispatch()
	const { selectBikeFleetAll, currentBikeFleetStatusSelected, bikeFleet } = useAppSelector(
		(state) => state.bikeFleet
	)
	const [{ status }] = useCurrentSearchParams(defaultBikeFleetQuery)
	const { t } = useTranslation()
	const { alert } = useAlert()
	const { entity } = useAuth()

	const onToggleCheckedAll = () => {
		dispatch(setToggleCheckedAll(status))
	}

	const onToggleChoice = (id_fnuci: string) => {
		dispatch(setToggleCheckedByCodeFnucy({ id_fnuci, status }))
	}

	React.useImperativeHandle(ref, () => ({ resetPage: () => gotoPage(0) }), [])

	const data = React.useMemo(() => datas, [datas])

	const viewProofRegistration = React.useCallback(
		async (fnuciCode: string) => {
			const bike = bikeFleet.find((bike) => bike.bike_id === fnuciCode) as Bike

			const gender =
				bike?.operator &&
				entity?.data_operators &&
				entity.data_operators.find((b) => b.operator === bike.operator)?.entity_data
					?.operator_gender

			const operatorGender = gender ?? " "

			// Manage pdf generation
			const timeStamp = dateService.getCurrentDate("dd-MM-y-HH'h'mm")
			const generateBikePDF =
				bike &&
				operatorGender &&
				(await pdf(<BikePdf bike={bike} entity={entity} operatorGender={operatorGender} />)
					.toBlob()
					.then((blob) => {
						saveBlob(blob, `imprimer-recapitulatif-${bike?.bike_id}-${timeStamp}.pdf`, {
							targetFile: false,
						})
						return blob
					})
					.catch((e) => null))

			if (!generateBikePDF) {
				alert("error", t("pdf.pdf_error", { ns: "bike" }))
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[bikeFleet, entity]
	)

	const columns = useColumn(
		onToggleChoice,
		onToggleCheckedAll,
		selectBikeFleetAll,
		status,
		currentBikeFleetStatusSelected,
		t as unknown as TFunction,
		viewProofRegistration
	)
	const getColumnSize = useColumnSize<Column>(columns)

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		prepareRow,
		pageOptions,
		gotoPage,
		state: { pageIndex },
	} = useTable(
		{
			autoResetPage: false,
			autoResetSortBy: false,
			//@ts-ignore
			columns,
			data,
			initialState: { pageIndex: 0, pageSize: 15 },
		},

		useSortBy,
		usePagination
	)

	return (
		<div
			className={clsx({
				"px-8": !isMobileOnly,
				"px-6": isMobileOnly,
			})}
		>
			<StatusBikeFleet />

			<div className="c-card p-5 md:p-12 mt-4">
				<SearchBikeFleet onResetPage={() => gotoPage(0)} />
				<MessageStatusBikeFleet />

				<div className={"c-history__table mt-8"}>
					<table className="c-table c-table--responsive" {...getTableProps()}>
						<>
							<HeaderTableBikeFleet
								headerGroups={headerGroups}
								getColumnSize={getColumnSize}
							/>
							<BodyTableBikeFleet
								getTableBodyProps={getTableBodyProps}
								page={page}
								prepareRow={prepareRow}
								getColumnSize={getColumnSize}
							/>
						</>
					</table>
					<NoBikeFleet hasNotBikeFleetIntoStatus={!isNotEmpty(data)} />
					{datas.length > 0 && (
						<ControlPagingBikeFleet
							gotoPage={gotoPage}
							pageIndex={pageIndex}
							pageOptions={pageOptions}
						/>
					)}
				</div>
			</div>
		</div>
	)
}

export default React.forwardRef(TableBikeFleet)
